import { API_BASE_URL } from '../config/api';
import { getStoredToken } from './authService';

export interface ImageUploadResponse {
  id: number;
  imageUrl: string;
  mimeType: string;
  originalName: string;
  status: string;
}

export interface ImageMetadata {
  width?: number;
  height?: number;
  originalName: string;
  mimeType: string;
}

/**
 * Upload an image file to the server
 * @param file - The image file to upload
 * @returns Promise with image URL and metadata
 */
export async function uploadImage(file: File): Promise<{ imageUrl: string; metadata: ImageMetadata }> {
  // Validate file type
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }

  // Validate file size (10MB limit)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    throw new Error('Image size must be less than 10MB');
  }

  // Create FormData
  const formData = new FormData();
  formData.append('image', file);

  // Get image dimensions before upload
  const imageMetadata = await getImageMetadata(file);

  // Upload image using custom fetch (FormData needs special handling)
  const token = getStoredToken();
  const response = await fetch(`${API_BASE_URL}/images/upload`, {
    method: 'POST',
    headers: {
      ...(token && { Authorization: `Bearer ${token}` })
      // Don't set Content-Type - browser will set it with boundary for FormData
    },
    body: formData
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication expired. Please log in again.');
    }
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to upload image');
  }

  const uploadResponse = result.data as ImageUploadResponse;

  return {
    imageUrl: uploadResponse.imageUrl,
    metadata: {
      width: imageMetadata.width,
      height: imageMetadata.height,
      originalName: uploadResponse.originalName,
      mimeType: uploadResponse.mimeType
    }
  };
}

/**
 * Get image metadata (dimensions) from a file
 */
function getImageMetadata(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.width,
        height: img.height
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Upload image from clipboard (File or Blob)
 */
export async function uploadImageFromBlob(blob: Blob, filename: string = 'pasted-image.png'): Promise<{ imageUrl: string; metadata: ImageMetadata }> {
  // Convert blob to file
  const file = new File([blob], filename, { type: blob.type || 'image/png' });
  return uploadImage(file);
}

